import { useState, useCallback, useRef, useEffect } from 'react'
import Container from '@cloudscape-design/components/container'
import Header from '@cloudscape-design/components/header'
import SpaceBetween from '@cloudscape-design/components/space-between'
import FormField from '@cloudscape-design/components/form-field'
import Input from '@cloudscape-design/components/input'
import Select from '@cloudscape-design/components/select'
import Textarea from '@cloudscape-design/components/textarea'
import Button from '@cloudscape-design/components/button'
import Toggle from '@cloudscape-design/components/toggle'
import Box from '@cloudscape-design/components/box'
import type { Profile, ProfileType, ImageAttachment, DocumentAttachment } from '@/types'
import { PROMPT_PRESETS, type PromptComplexity } from '@/config/defaults'
import { getEolWarning, type LifecycleModel } from '@/components/lifecycle/ModelLifecyclePage'
import { API_BASE } from '@/config/api'

interface Props {
  profile?: Profile
  onSave: (profile: Profile) => void
  onCancel: () => void
}

const PROFILE_TYPES: { value: ProfileType; label: string }[] = [
  { value: 'anthropic-direct', label: 'Direct Anthropic API' },
  { value: 'bedrock', label: 'Bedrock - InvokeModelWithResponseStream' },
  { value: 'bedrock-guardrail', label: 'Bedrock - InvokeModelWithResponseStream + Guardrail' },
  { value: 'bedrock-converse', label: 'Bedrock - ConverseStream' },
  { value: 'grok', label: 'Grok (xAI)' }
]

interface ModelOption { id: string; label: string; group: string }

const MODEL_CATALOG: ModelOption[] = [
  { id: 'us.anthropic.claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (US)', group: 'Bedrock Cross-Region (US)' },
  { id: 'us.anthropic.claude-sonnet-4-5-20250929-v1:0', label: 'Claude Sonnet 4.5 (US)', group: 'Bedrock Cross-Region (US)' },
  { id: 'us.anthropic.claude-sonnet-4-20250514-v1:0', label: 'Claude Sonnet 4 (US)', group: 'Bedrock Cross-Region (US)' },
  { id: 'us.anthropic.claude-opus-4-6-v1', label: 'Claude Opus 4.6 (US)', group: 'Bedrock Cross-Region (US)' },
  { id: 'us.anthropic.claude-opus-4-5-20251101-v1:0', label: 'Claude Opus 4.5 (US)', group: 'Bedrock Cross-Region (US)' },
  { id: 'us.anthropic.claude-haiku-4-5-20251001-v1:0', label: 'Claude Haiku 4.5 (US)', group: 'Bedrock Cross-Region (US)' },
  { id: 'eu.anthropic.claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (EU)', group: 'Bedrock Cross-Region (EU)' },
  { id: 'eu.anthropic.claude-opus-4-5-20251101-v1:0', label: 'Claude Opus 4.5 (EU)', group: 'Bedrock Cross-Region (EU)' },
  { id: 'eu.anthropic.claude-haiku-4-5-20251001-v1:0', label: 'Claude Haiku 4.5 (EU)', group: 'Bedrock Cross-Region (EU)' },
  { id: 'ap.anthropic.claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (APAC)', group: 'Bedrock Cross-Region (APAC)' },
  { id: 'ap.anthropic.claude-sonnet-4-5-20250929-v1:0', label: 'Claude Sonnet 4.5 (APAC)', group: 'Bedrock Cross-Region (APAC)' },
  { id: 'ap.anthropic.claude-opus-4-5-20251101-v1:0', label: 'Claude Opus 4.5 (APAC)', group: 'Bedrock Cross-Region (APAC)' },
  { id: 'ap.anthropic.claude-haiku-4-5-20251001-v1:0', label: 'Claude Haiku 4.5 (APAC)', group: 'Bedrock Cross-Region (APAC)' },
  { id: 'global.anthropic.claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (Global)', group: 'Bedrock Global CRIS' },
  { id: 'global.anthropic.claude-opus-4-6-v1', label: 'Claude Opus 4.6 (Global)', group: 'Bedrock Global CRIS' },
  { id: 'global.anthropic.claude-opus-4-5-20251101-v1:0', label: 'Claude Opus 4.5 (Global)', group: 'Bedrock Global CRIS' },
  { id: 'global.anthropic.claude-haiku-4-5-20251001-v1:0', label: 'Claude Haiku 4.5 (Global)', group: 'Bedrock Global CRIS' },
  { id: 'global.anthropic.claude-sonnet-4-20250514-v1:0', label: 'Claude Sonnet 4 (Global)', group: 'Bedrock Global CRIS' },
  { id: 'global.anthropic.claude-sonnet-4-5-20250929-v1:0', label: 'Claude Sonnet 4.5 (Global)', group: 'Bedrock Global CRIS' },
  { id: 'anthropic.claude-sonnet-4-6', label: 'Claude Sonnet 4.6', group: 'Bedrock Single-Region' },
  { id: 'anthropic.claude-opus-4-6-v1', label: 'Claude Opus 4.6', group: 'Bedrock Single-Region' },
  { id: 'anthropic.claude-opus-4-5-20251101-v1:0', label: 'Claude Opus 4.5', group: 'Bedrock Single-Region' },
  { id: 'anthropic.claude-haiku-4-5-20251001-v1:0', label: 'Claude Haiku 4.5', group: 'Bedrock Single-Region' },
  { id: 'anthropic.claude-sonnet-4-20250514-v1:0', label: 'Claude Sonnet 4', group: 'Bedrock Single-Region' },
  { id: 'anthropic.claude-sonnet-4-5-20250929-v1:0', label: 'Claude Sonnet 4.5', group: 'Bedrock Single-Region' },
  { id: 'anthropic.claude-opus-4-1-20250805-v1:0', label: 'Claude Opus 4.1', group: 'Bedrock Single-Region' },
  { id: 'us.amazon.nova-pro-v1:0', label: 'Nova Pro (US)', group: 'Bedrock Amazon Nova' },
  { id: 'us.amazon.nova-lite-v1:0', label: 'Nova Lite (US)', group: 'Bedrock Amazon Nova' },
  { id: 'us.amazon.nova-micro-v1:0', label: 'Nova Micro (US)', group: 'Bedrock Amazon Nova' },
  { id: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4', group: 'Anthropic' },
  { id: 'claude-sonnet-4-6-20250725', label: 'Claude Sonnet 4.6', group: 'Anthropic' },
  { id: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5', group: 'Anthropic' },
  { id: 'claude-opus-4-5-20251101', label: 'Claude Opus 4.5', group: 'Anthropic' },
  { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5', group: 'Anthropic' },
  { id: 'grok-3-mini-fast', label: 'Grok 3 Mini Fast', group: 'Grok (xAI)' },
  { id: 'grok-3-fast', label: 'Grok 3 Fast', group: 'Grok (xAI)' },
  { id: 'grok-3', label: 'Grok 3', group: 'Grok (xAI)' },
  { id: 'grok-4-fast-non-reasoning', label: 'Grok 4 Fast (non-reasoning)', group: 'Grok (xAI)' },
  { id: 'grok-4-fast', label: 'Grok 4 Fast', group: 'Grok (xAI)' },
  { id: 'grok-4', label: 'Grok 4', group: 'Grok (xAI)' },
]

const AWS_REGIONS = [
  { id: 'us-east-1', label: 'US East (N. Virginia)' },
  { id: 'us-east-2', label: 'US East (Ohio)' },
  { id: 'us-west-1', label: 'US West (N. California)' },
  { id: 'us-west-2', label: 'US West (Oregon)' },
  { id: 'af-south-1', label: 'Africa (Cape Town)' },
  { id: 'ap-east-1', label: 'Asia Pacific (Hong Kong)' },
  { id: 'ap-east-2', label: 'Asia Pacific (Hyderabad)' },
  { id: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
  { id: 'ap-south-2', label: 'Asia Pacific (Hyderabad)' },
  { id: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { id: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
  { id: 'ap-southeast-3', label: 'Asia Pacific (Jakarta)' },
  { id: 'ap-southeast-4', label: 'Asia Pacific (Melbourne)' },
  { id: 'ap-southeast-5', label: 'Asia Pacific (Malaysia)' },
  { id: 'ap-southeast-7', label: 'Asia Pacific (Thailand)' },
  { id: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { id: 'ap-northeast-2', label: 'Asia Pacific (Seoul)' },
  { id: 'ap-northeast-3', label: 'Asia Pacific (Osaka)' },
  { id: 'ca-central-1', label: 'Canada (Central)' },
  { id: 'ca-west-1', label: 'Canada (Calgary)' },
  { id: 'eu-central-1', label: 'Europe (Frankfurt)' },
  { id: 'eu-central-2', label: 'Europe (Zurich)' },
  { id: 'eu-west-1', label: 'Europe (Ireland)' },
  { id: 'eu-west-2', label: 'Europe (London)' },
  { id: 'eu-west-3', label: 'Europe (Paris)' },
  { id: 'eu-south-1', label: 'Europe (Milan)' },
  { id: 'eu-south-2', label: 'Europe (Spain)' },
  { id: 'eu-north-1', label: 'Europe (Stockholm)' },
  { id: 'il-central-1', label: 'Israel (Tel Aviv)' },
  { id: 'me-south-1', label: 'Middle East (Bahrain)' },
  { id: 'me-central-1', label: 'Middle East (UAE)' },
  { id: 'mx-central-1', label: 'Mexico (Central)' },
  { id: 'sa-east-1', label: 'South America (São Paulo)' },
]

function getFilteredModelOptions(type: ProfileType) {
  return MODEL_CATALOG.filter(m => {
    if (type === 'grok') return m.group.startsWith('Grok')
    if (type === 'anthropic-direct') return m.group === 'Anthropic'
    return m.group.startsWith('Bedrock') || m.group.startsWith('Amazon')
  })
}

function buildModelSelectOptions(type: ProfileType) {
  const filtered = getFilteredModelOptions(type)
  const groups = [...new Set(filtered.map(m => m.group))]
  return [
    { value: '__custom__', label: '- Custom (type below) -' },
    ...groups.flatMap(g =>
      filtered.filter(m => m.group === g).map(m => ({
        value: m.id,
        label: m.label,
        description: g,
      }))
    )
  ]
}

export function ProfileEditor({ profile, onSave, onCancel }: Props) {
  const isNew = !profile
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [form, setForm] = useState<Profile>(
    profile ?? {
      id: `profile-${Date.now()}`,
      name: '',
      type: 'bedrock',
      region: 'us-east-1',
      modelId: '',
      maxTokens: 256,
      prompt: 'Count from 1 to 10.',
      enabled: true,
      imageAttachments: []
    }
  )

  const detectComplexity = (prompt: string): PromptComplexity => {
    for (const [key, preset] of Object.entries(PROMPT_PRESETS)) {
      if (preset.prompt === prompt) return key as PromptComplexity
    }
    return 'custom'
  }
  const [complexity, setComplexity] = useState<PromptComplexity>(detectComplexity(form.prompt))

  const set = (key: keyof Profile, value: unknown) => setForm((f) => ({ ...f, [key]: value }))

  const handleComplexityChange = useCallback((value: PromptComplexity) => {
    setComplexity(value)
    if (value !== 'custom') {
      const preset = PROMPT_PRESETS[value]
      setForm((f) => ({ ...f, prompt: preset.prompt, maxTokens: preset.maxTokens }))
    }
  }, [])

  const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return
    const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    const MAX_SIZE = 10 * 1024 * 1024

    Array.from(files).forEach(file => {
      if (!ACCEPTED_TYPES.includes(file.type)) return
      if (file.size > MAX_SIZE) return
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1]
        const attachment: ImageAttachment = {
          base64,
          mediaType: file.type as ImageAttachment['mediaType'],
          name: file.name,
          sizeBytes: file.size,
        }
        setForm(f => ({
          ...f,
          imageAttachments: [...(f.imageAttachments || []), attachment]
        }))
      }
      reader.readAsDataURL(file)
    })
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  const removeImage = useCallback((index: number) => {
    setForm(f => ({
      ...f,
      imageAttachments: (f.imageAttachments || []).filter((_, i) => i !== index)
    }))
  }, [])

  const totalImageSize = (form.imageAttachments || []).reduce((sum, img) => sum + img.sizeBytes, 0)

  const [lifecycleModels, setLifecycleModels] = useState<LifecycleModel[]>([])
  useEffect(() => {
    fetch(`${API_BASE}/model-lifecycle`).then(r => r.json()).then(d => setLifecycleModels(d.models || [])).catch(() => {})
  }, [])
  const eolWarning = getEolWarning(form.modelId, lifecycleModels)

  const selectedModelOption = MODEL_CATALOG.some(m => m.id === form.modelId)
    ? { value: form.modelId, label: MODEL_CATALOG.find(m => m.id === form.modelId)?.label ?? form.modelId }
    : { value: '__custom__', label: '- Custom (type below) -' }

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label={isNew ? 'Add Profile' : 'Edit Profile'}>
      <div style={modalStyle}>
        <Container
          header={<Header variant="h2">{isNew ? 'Add Profile' : 'Edit Profile'}</Header>}
        >
          <SpaceBetween size="m">
            <FormField label="Name">
              <Input value={form.name} onChange={({ detail }) => set('name', detail.value)} />
            </FormField>

            <FormField label="Type">
              <Select
                selectedOption={{ value: form.type, label: PROFILE_TYPES.find(t => t.value === form.type)?.label ?? form.type }}
                onChange={({ detail }) => set('type', detail.selectedOption.value as ProfileType)}
                options={PROFILE_TYPES.map(t => ({ value: t.value, label: t.label }))}
              />
            </FormField>

            <FormField
              label="Region"
              description={
                form.type !== 'grok' && form.type !== 'anthropic-direct'
                  ? (/^(us|eu|ap|apac|global|jp|au|me|sa|af|ca)\./.test(form.modelId)
                    ? '💡 Cross-region model ID detected - Bedrock routes automatically, region is used for the API endpoint only.'
                    : '⚠️ Single-region model - make sure this model is available in the selected region.')
                  : undefined
              }
            >
              <Select
                selectedOption={{ value: form.region, label: `${form.region} - ${AWS_REGIONS.find(r => r.id === form.region)?.label ?? ''}` }}
                onChange={({ detail }) => set('region', detail.selectedOption.value)}
                options={AWS_REGIONS.map(r => ({ value: r.id, label: `${r.id} - ${r.label}` }))}
                disabled={form.type === 'grok'}
              />
            </FormField>

            <FormField label={form.type === 'grok' ? 'Model ID' : 'Model ID / ARN'}>
              <SpaceBetween size="xs">
                <Select
                  selectedOption={selectedModelOption}
                  onChange={({ detail }) => {
                    if (detail.selectedOption.value === '__custom__') return
                    set('modelId', detail.selectedOption.value)
                    const match = MODEL_CATALOG.find(m => m.id === detail.selectedOption.value)
                    if (match) set('name', match.label)
                  }}
                  options={buildModelSelectOptions(form.type)}
                />
                <Input
                  value={form.modelId}
                  onChange={({ detail }) => set('modelId', detail.value)}
                  placeholder={'Or paste a custom model ID / ARN'}
                />
              </SpaceBetween>
            </FormField>
            {eolWarning && (
              <Box variant="small" color={eolWarning.startsWith('⛔') ? 'text-status-error' : 'text-status-warning'}>
                {eolWarning}
              </Box>
            )}

            {(form.type === 'bedrock-guardrail' || form.type === 'bedrock-proxy') && (
              <>
                <FormField label="Guardrail ID">
                  <Input value={form.guardrailId ?? ''} onChange={({ detail }) => set('guardrailId', detail.value)} />
                </FormField>
                <FormField label="Guardrail Version">
                  <Input value={form.guardrailVersion ?? 'DRAFT'} onChange={({ detail }) => set('guardrailVersion', detail.value)} />
                </FormField>
              </>
            )}

            {form.type === 'bedrock-proxy' && (
              <FormField label="Proxy URL">
                <Input value={form.proxyUrl ?? ''} onChange={({ detail }) => set('proxyUrl', detail.value)} placeholder="http://proxy.example.com:8080" />
              </FormField>
            )}

            <FormField label="Prompt Complexity">
              <SpaceBetween direction="horizontal" size="xs">
                {(['simple', 'medium', 'complex', 'custom'] as PromptComplexity[]).map((level) => (
                  <Button
                    key={level}
                    variant={complexity === level ? 'primary' : 'normal'}
                    onClick={() => handleComplexityChange(level)}
                  >
                    {level === 'custom' ? 'Custom' : PROMPT_PRESETS[level as keyof typeof PROMPT_PRESETS].label}
                  </Button>
                ))}
              </SpaceBetween>
            </FormField>

            <FormField label="Max Tokens">
              <Input
                type="number"
                value={String(form.maxTokens)}
                onChange={({ detail }) => { set('maxTokens', parseInt(detail.value, 10) || 256); setComplexity('custom') }}
              />
            </FormField>

            <FormField label="Prompt">
              <SpaceBetween size="xs">
                <Textarea
                  value={form.prompt}
                  onChange={({ detail }) => { set('prompt', detail.value); setComplexity('custom') }}
                  rows={4}
                />
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="normal" iconName="upload" onClick={() => {
                    const input = document.createElement('input')
                    input.type = 'file'
                    input.accept = '.txt,.md,.json,.csv,.log,.xml,.html,.py,.js,.ts,.java,.go,.rs'
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0]
                      if (!file) return
                      const reader = new FileReader()
                      reader.onload = () => {
                        set('prompt', reader.result as string)
                        setComplexity('custom')
                      }
                      reader.readAsText(file)
                    }
                    input.click()
                  }}>Load from file</Button>
                  {form.prompt.length > 1000 && (
                    <Box variant="small" color="text-body-secondary">
                      {`${(form.prompt.length / 1000).toFixed(0)}K chars (~${Math.round(form.prompt.length / 4)} tokens est.)`}
                    </Box>
                  )}
                </SpaceBetween>
              </SpaceBetween>
            </FormField>

            <FormField label="Image Attachments (multimodal)">
              <SpaceBetween size="xs">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  multiple
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="normal" iconName="upload" onClick={() => fileInputRef.current?.click()}>
                    Add Images
                  </Button>
                  {totalImageSize > 0 && (
                    <Box variant="small" color="text-body-secondary">{(totalImageSize / (1024 * 1024)).toFixed(1)} MB total</Box>
                  )}
                </SpaceBetween>
                {(form.imageAttachments || []).map((img, i) => (
                  <SpaceBetween key={i} direction="horizontal" size="xs">
                    <Box variant="small" color="text-body-secondary">{img.name} ({(img.sizeBytes / 1024).toFixed(0)} KB)</Box>
                    <Button variant="icon" iconName="close" onClick={() => removeImage(i)} ariaLabel={`Remove ${img.name}`} />
                  </SpaceBetween>
                ))}
                {totalImageSize > 5 * 1024 * 1024 && (
                  <Box variant="small" color="text-status-warning">
                    Large payload ({(totalImageSize / (1024 * 1024)).toFixed(1)} MB) - useful for testing Bedrock buffering behavior with large images
                  </Box>
                )}
              </SpaceBetween>
            </FormField>

            <FormField label="PDF Document Attachments">
              <SpaceBetween size="xs">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="normal" iconName="upload" onClick={() => {
                    const input = document.createElement('input')
                    input.type = 'file'
                    input.accept = 'application/pdf'
                    input.multiple = true
                    input.onchange = (ev) => {
                      const files = (ev.target as HTMLInputElement).files
                      if (!files) return
                      Array.from(files).forEach(file => {
                        if (file.type !== 'application/pdf' || file.size > 30 * 1024 * 1024) return
                        const reader = new FileReader()
                        reader.onload = () => {
                          const b64 = (reader.result as string).split(',')[1]
                          const doc: DocumentAttachment = { base64: b64, mediaType: 'application/pdf', name: file.name, sizeBytes: file.size }
                          setForm(f => ({ ...f, documentAttachments: [...(f.documentAttachments || []), doc] }))
                        }
                        reader.readAsDataURL(file)
                      })
                    }
                    input.click()
                  }}>Add PDFs</Button>
                  {(form.documentAttachments || []).length > 0 && (
                    <Box variant="small" color="text-body-secondary">
                      {((form.documentAttachments || []).reduce((s, d) => s + d.sizeBytes, 0) / (1024 * 1024)).toFixed(1)} MB
                    </Box>
                  )}
                </SpaceBetween>
                {(form.documentAttachments || []).map((doc, i) => (
                  <SpaceBetween key={i} direction="horizontal" size="xs">
                    <Box variant="small" color="text-body-secondary">{doc.name} ({(doc.sizeBytes / 1024).toFixed(0)} KB)</Box>
                    <Button variant="icon" iconName="close" onClick={() => setForm(f => ({ ...f, documentAttachments: (f.documentAttachments || []).filter((_, j) => j !== i) }))} ariaLabel={`Remove ${doc.name}`} />
                  </SpaceBetween>
                ))}
              </SpaceBetween>
            </FormField>

            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onCancel}>Cancel</Button>
              <Button variant="primary" onClick={() => onSave(form)}>{isNew ? 'Add' : 'Save'}</Button>
            </SpaceBetween>
          </SpaceBetween>
        </Container>
      </div>
    </div>
  )
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100
}

const modalStyle: React.CSSProperties = {
  width: 500, maxHeight: '80vh', overflowY: 'auto'
}
